import { useMMDataContext } from 'src/features/MM/contexts/MMDataContext';
import { useCPULabel } from 'src/features/MM/hooks/useCPULabel';
import { useChartEffectTickFormatter } from 'src/hooks/useEffectFormatter';
import { useKpiUnit } from 'src/hooks/useKpiUnit';
import {
  EuroValueTickFormatter,
  PercentageValueTickFormatter,
  ValueTickFormatter,
} from 'src/utils/TickFormatters';
import { useRoiDef } from 'src/utils/calculateRoi';
import {
  INCREMENTAL_SALES_DEF,
  MEDIA_EFFECT_DEF,
  MEDIA_INVESTMENT_DEF,
  ROMI_DEF,
  TOTAL_ROI_DEF,
  TOTAL_ROMI_DEF,
  getCPUDef,
  getTotalCPUDef,
} from 'src/utils/metricDefinitions';
import { TrendsChartBox } from '../../organisms/TrendsChartBox';
import { TrendTabs, TrendsType } from '../../molecules/TrendTabs';
import { CumulativeSpendChart } from '../../molecules/CumulativeSpendChart';
import { PacingHealthChart } from '../../molecules/PacingHealthChart';
import { DailySpendWithROIChart } from '../../molecules/DailySpendWithROIChart';
import { WeeklySpendHeatmap } from 'src/components/molecules/WeeklySpendHeatmap';
import { useState } from 'react';
import { Box, Grid, GridItem, Text, useTheme } from '@chakra-ui/react';
import { CampaignCalendar } from 'src/components/molecules/CampaignCalendar';
import {
  mockChannels,
  mockEvents,
} from 'src/components/molecules/CampaignCalendar/mockData';

const colSpan = 12;
const minH = 350;

// Mock data for pacing view - this should be replaced with actual data from API
const mockCumulativeSpendData = [
  {
    date: '2024-05-01',
    utcEpoch: 1714521600000,
    actualSpend: 320,
    idealSpend: 270,
  },
  {
    date: '2024-05-02',
    utcEpoch: 1714608000000,
    actualSpend: 310,
    idealSpend: 300,
  },
  {
    date: '2024-05-03',
    utcEpoch: 1714694400000,
    actualSpend: 390,
    idealSpend: 370,
  },
  {
    date: '2024-05-04',
    utcEpoch: 1714780800000,
    actualSpend: 450,
    idealSpend: 400,
  },
  {
    date: '2024-05-05',
    utcEpoch: 1714867200000,
    actualSpend: 350,
    idealSpend: 320,
  },
  {
    date: '2024-05-06',
    utcEpoch: 1714953600000,
    actualSpend: 290,
    idealSpend: 240,
  },
  {
    date: '2024-05-07',
    utcEpoch: 1715040000000,
    actualSpend: 430,
    idealSpend: 380,
  },
];

const mockPacingHealthData = {
  actualSpend: 310,
  idealSpend: 300,
  pacingHealth: 53,
};

// Mock data for Daily Spend with ROI chart
const mockDailySpendWithROIData = [
  {
    date: '2024-05-01',
    utcEpoch: 1714521600000,
    dailySpend: 90,
    roi: 0.6,
  },
  {
    date: '2024-05-02',
    utcEpoch: 1714608000000,
    dailySpend: 100,
    roi: 1.0,
  },
  {
    date: '2024-05-03',
    utcEpoch: 1714694400000,
    dailySpend: 90,
    roi: 0.8,
  },
  {
    date: '2024-05-04',
    utcEpoch: 1714780800000,
    dailySpend: 80,
    roi: 0.7,
  },
  {
    date: '2024-05-05',
    utcEpoch: 1714867200000,
    dailySpend: 70,
    roi: 0.6,
  },
  {
    date: '2024-05-06',
    utcEpoch: 1714953600000,
    dailySpend: 85,
    roi: 0.8,
  },
  {
    date: '2024-05-07',
    utcEpoch: 1715040000000,
    dailySpend: 60,
    roi: 0.7,
  },
];

// Mock data for Weekly Spend Heatmap
const mockWeeklySpendHeatmapData = [
  {
    day: 'Mon',
    category1: 6.2,
    category2: 4.4,
    category3: 3.2,
  },
  {
    day: 'Tue',
    category1: 3.2,
    category2: 4.4,
    category3: 9.2,
  },
  {
    day: 'Wed',
    category1: 9.2,
    category2: 6.2,
    category3: 4.4,
  },
  {
    day: 'Thu',
    category1: 3.2,
    category2: 1.5,
    category3: 4.4,
  },
  {
    day: 'Fri',
    category1: 9.2,
    category2: 4.4,
    category3: 6.2,
  },
  {
    day: 'Sat',
    category1: 4.4,
    category2: 3.2,
    category3: 6.2,
  },
  {
    day: 'Sun',
    category1: 3.2,
    category2: 1.5,
    category3: 6.2,
  },
];

export const Trends: React.FC = () => {
  const cpuLabel = useCPULabel();
  const theme = useTheme();
  const { roi, romi, romiEnabled, mediaEffect, cpu, decomps, investments } =
    useMMDataContext();

  const { unit } = useKpiUnit();

  const unitTabLabel = unit === 'euro' ? 'Euro' : 'Unit';

  const incrementalSalesTickFormatter = useChartEffectTickFormatter();

  const roiDef = useRoiDef();

  const [selectedTab, setSelectedTab] = useState<TrendsType>('trends');

  return (
    <>
      <TrendTabs onSelect={setSelectedTab} selectedTab={selectedTab} />
      {selectedTab === 'trends' ? (
        <>
          {romiEnabled ? (
            <TrendsChartBox
              title="ROMI"
              totalLineLabel="Total ROMI"
              colSpan={colSpan}
              minH={minH}
              metricState={romi}
              yAxisFormatter={ValueTickFormatter}
              infoBodyText={`${ROMI_DEF}\n\n${TOTAL_ROMI_DEF}`}
              infoTitle="Return on Marketing Investment"
            />
          ) : (
            <TrendsChartBox
              title="ROI"
              totalLineLabel="Total ROI"
              colSpan={colSpan}
              minH={minH}
              metricState={roi}
              yAxisFormatter={ValueTickFormatter}
              infoBodyText={`${roiDef}\n\n${TOTAL_ROI_DEF}`}
              infoTitle="Return on Investment"
            />
          )}
          <TrendsChartBox
            title="Media investment"
            colSpan={colSpan}
            minH={minH}
            metricState={investments}
            unitTabLabel={'Euro'}
            yAxisFormatter={EuroValueTickFormatter}
            infoBodyText={MEDIA_INVESTMENT_DEF}
          />
          <TrendsChartBox
            title="Incremental sales"
            colSpan={colSpan}
            minH={minH}
            metricState={decomps}
            unitTabLabel={unitTabLabel}
            yAxisFormatter={incrementalSalesTickFormatter}
            infoBodyText={INCREMENTAL_SALES_DEF}
          />
          <TrendsChartBox
            title={cpuLabel}
            totalLineLabel={`Total ${cpuLabel}`}
            colSpan={colSpan}
            minH={minH}
            metricState={cpu}
            yAxisFormatter={EuroValueTickFormatter}
            infoBodyText={`${getCPUDef(cpuLabel)}\n\n${getTotalCPUDef(cpuLabel)}`}
          />
          <TrendsChartBox
            title={'Media effect'}
            colSpan={colSpan}
            minH={minH}
            metricState={mediaEffect}
            yAxisFormatter={PercentageValueTickFormatter}
            infoBodyText={MEDIA_EFFECT_DEF}
          />
        </>
      ) : selectedTab === 'pacingView' ? (
        <>
          <GridItem colSpan={12}>
            <Grid templateColumns="repeat(12, 1fr)" gap={6} templateRows="1fr">
              <GridItem colSpan={8}>
                <Box
                  bg="surface.card"
                  borderRadius="lg"
                  p={6}
                  boxShadow="sm"
                  border="1px solid"
                  borderColor="border.layout"
                  height="100%"
                  display="flex"
                  flexDirection="column">
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={4}>
                    <Text fontSize="lg" fontWeight="semibold">
                      Cumulative Spend: Actual vs Ideal
                    </Text>
                    <Box display="flex" alignItems="center" gap={4}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box
                          w="3"
                          h="3"
                          bg={theme.colors.chart.line.blue}
                          borderRadius="sm"
                        />
                        <Text fontSize="sm" color="gray.600">
                          Actual
                        </Text>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box w="3" h="3" bg="#22c55e" borderRadius="sm" />
                        <Text fontSize="sm" color="gray.600">
                          Ideal
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                  <Box flex="1">
                    <CumulativeSpendChart data={mockCumulativeSpendData} />
                  </Box>
                </Box>
              </GridItem>
              <GridItem colSpan={4}>
                <Box
                  bg="surface.card"
                  borderRadius="lg"
                  p={6}
                  boxShadow="sm"
                  border="1px solid"
                  borderColor="border.layout"
                  height="100%"
                  display="flex"
                  flexDirection="column">
                  <Text fontSize="lg" fontWeight="semibold" mb={4}>
                    Pacing Health
                  </Text>
                  <Box flex="1">
                    <PacingHealthChart data={mockPacingHealthData} />
                  </Box>
                </Box>
              </GridItem>
            </Grid>
          </GridItem>

          {/* Daily Spend with ROI Chart */}
          <GridItem colSpan={12}>
            <Box
              bg="surface.card"
              borderRadius="lg"
              p={6}
              boxShadow="sm"
              border="1px solid"
              borderColor="border.layout"
              height="100%"
              display="flex"
              flexDirection="column">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={4}>
                <Text fontSize="lg" fontWeight="semibold">
                  Daily Spend with ROI
                </Text>
                <Box display="flex" alignItems="center" gap={4}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      w="3"
                      h="3"
                      bg={theme.colors.chart.line.blue}
                      borderRadius="sm"
                    />
                    <Text fontSize="sm" color="gray.600">
                      ROI
                    </Text>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      w="3"
                      h="3"
                      bg={theme.colors.chart.bar.lightGreen}
                      borderRadius="sm"
                    />
                    <Text fontSize="sm" color="gray.600">
                      Daily Spend
                    </Text>
                  </Box>
                </Box>
              </Box>
              <Box flex="1">
                <DailySpendWithROIChart data={mockDailySpendWithROIData} />
              </Box>
            </Box>
          </GridItem>

          {/* Weekly Spend Heatmap */}
          <GridItem colSpan={12}>
            <Box
              bg="surface.card"
              borderRadius="lg"
              p={6}
              boxShadow="sm"
              border="1px solid"
              borderColor="border.layout"
              height="100%"
              display="flex"
              flexDirection="column">
              <Box flex="1">
                <WeeklySpendHeatmap data={mockWeeklySpendHeatmapData} />
              </Box>
            </Box>
          </GridItem>
        </>
      ) : (
        <GridItem colSpan={12}>
          <CampaignCalendar
            startDate="2026-08-03"
            endDate="2026-08-10"
            channels={mockChannels}
            events={mockEvents}
          />
        </GridItem>
      )}
    </>
  );
};
